import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import { useWatch } from "antd/es/form/Form";
import useFormInstance from "antd/es/form/hooks/useFormInstance";
import { defineMessages, useIntl } from "react-intl";

import FieldVisibility, { FieldTeamVisibility } from "@mapmycustomers/shared/enum/FieldVisibility";
import SchemaFieldCategory from "@mapmycustomers/shared/enum/SchemaFieldCategory";
import { EntityTypesSupportingFieldCustomization } from "@mapmycustomers/shared/types/entity";
import Role from "@mapmycustomers/shared/types/Role";
import FieldTeamVisibilityRecord from "@mapmycustomers/shared/types/schema/FieldTeamVisibilityRecord";
import SchemaField from "@mapmycustomers/shared/types/schema/SchemaField";
import Team from "@mapmycustomers/shared/types/Team";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { Alert } from "@mapmycustomers/ui";

import DefaultCustomFieldFormValues from "@app/scene/settings/component/CustomFields/type/DefaultCustomFieldFormValues";
import { getActiveEntityType } from "@app/scene/settings/component/ManageFields/store";
import { doesOrganizationSupportHipaa } from "@app/store/iam";
import { getRolesExcludingOwners, getTeams } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import {
  buildFieldVisibilityRecordForTeamRole,
  createTeamVisibilityFromRoleVisibility,
} from "@app/util/schema/fieldVisibilityRecord";

import PhiSwitch from "./components/PhiSwitch";
import RoleVisibility from "./components/RoleVisibility";
import TeamVisibility from "./components/TeamVisibility";
import VisibilityRadioGroup from "./components/VisibilityRadioGroup";
import styles from "./FieldAccess.module.scss";

interface Props {
  calculatedField?: boolean;
  customField?: boolean;
  entityType: EntityTypesSupportingFieldCustomization;
  roles: Role[];
  schemaField?: SchemaField;
  selectedSchemaFields?: SchemaField[];
  supportsHipaa?: boolean;
  teams: Team[];
}

const messages = defineMessages({
  dealFunnelWarning: {
    id: "settings.manageFields.editPane.fieldAccess.dealFunnelWarning",
    defaultMessage:
      "If Deal Stage is View Only, Deal Funnel must be View Only; to allow more access options for Deal Funnel, change the Deal Stage access.",
    description: "Deal funnel warning when deal stage is view only",
  },
  dealStageWarning: {
    id: "settings.manageFields.editPane.fieldAccess.dealStageWarning",
    defaultMessage:
      "If Deal Funnel is View and Edit, then Deal Stage must be View and Edit; to allow more access options for Deal Stage, change the Deal Funnel access.",
    description: "Deal Stage warning when deal funnel is full access",
  },
  fieldAccess: {
    id: "settings.manageFields.editPane.fieldAccess.title",
    defaultMessage: "Field Access",
    description: "Settings Custom field - create/edit - share&save Field Access",
  },
  noPhiSupport: {
    id: "settings.manageFields.editPane.fieldAccess.noPhiSupport",
    defaultMessage: "PHI cannot be enabled for this field",
    description: "Settings Custom field - create/edit - Info for no PHI support",
  },
  phiWarning: {
    id: "settings.manageFields.bulkEditPane.fieldAccess.phi.warning",
    defaultMessage:
      "The following field(s) do not support PHI access options and <b> will not be updated </b> if you proceed with this change:",
    description: "warning message for bulk edit when toggled on",
  },
});

const FieldAccess: React.FC<Props> = ({
  calculatedField,
  customField,
  entityType,
  roles,
  schemaField,
  selectedSchemaFields,
  supportsHipaa,
  teams,
}) => {
  const intl = useIntl();

  const form = useFormInstance<DefaultCustomFieldFormValues>();
  const phiEnabled = useWatch("phiEnabled");
  const visibility = useWatch("visibility");
  const unifiedVisibility = useWatch("unifiedVisibility");

  const roleVisibilityVisible = visibility === FieldVisibility.TEAMS && unifiedVisibility;
  const teamVisibilityVisible = visibility === FieldVisibility.TEAMS && !unifiedVisibility;
  const fieldModel = getFieldModelByEntityType(entityType);

  const field = schemaField?.field ? fieldModel.getByPlatformName(schemaField.field) : undefined;
  const phiSupportedField = !!field?.doesSupportPhi || !!customField;

  const isDealStageFieldViewOnly = useMemo(() => {
    const dealSchemaField = fieldModel.getByName(DealFieldName.STAGE)?.schemaDetails;
    const viewOnlyTeamVisibility =
      !!dealSchemaField &&
      dealSchemaField.visibility === FieldVisibility.TEAMS &&
      (dealSchemaField.teamVisibility ?? []).some(
        ({ visibility }) => visibility === FieldVisibility.PUBLIC
      );
    return (
      field?.name === DealFieldName.FUNNEL &&
      !!dealSchemaField &&
      (dealSchemaField.visibility === FieldVisibility.PUBLIC || viewOnlyTeamVisibility)
    );
  }, [field?.name, fieldModel]);

  const isDealFunnelFieldFullAccess = useMemo(() => {
    const funnelSchemaField = fieldModel.getByName(DealFieldName.FUNNEL)?.schemaDetails;
    const fullAccessTeamVisibility =
      !!funnelSchemaField &&
      funnelSchemaField.visibility === FieldVisibility.TEAMS &&
      (funnelSchemaField.teamVisibility ?? []).some(
        ({ visibility }) => visibility === FieldVisibility.FULL_ACCESS
      );
    return (
      field?.name === DealFieldName.STAGE &&
      !!funnelSchemaField &&
      (funnelSchemaField.visibility === FieldVisibility.FULL_ACCESS || fullAccessTeamVisibility)
    );
  }, [field?.name, fieldModel]);

  const defaultVisibility = phiEnabled
    ? FieldVisibility.PRIVATE
    : calculatedField || isDealStageFieldViewOnly
    ? FieldVisibility.PUBLIC
    : FieldVisibility.FULL_ACCESS;

  const defaultTeamVisibility: FieldTeamVisibilityRecord[] = useMemo(
    () =>
      roles.reduce(
        (result, role) => [
          ...result,
          ...teams.map((team) =>
            buildFieldVisibilityRecordForTeamRole(team, role.id, defaultVisibility)
          ),
        ],
        [] as FieldTeamVisibilityRecord[]
      ),
    [defaultVisibility, roles, teams]
  );

  const defaultRoleVisibility = useMemo(
    () => roles.reduce((result, role) => ({ ...result, [role.id]: defaultVisibility }), {}),
    [defaultVisibility, roles]
  );

  const ensureVisibilityPresent = useDynamicCallback(
    (payload?: Partial<{ force: boolean; unifiedVisibility: boolean }>) => {
      const { force, unifiedVisibility } = payload ?? {};
      const isUnifiedVisibility = unifiedVisibility ?? form.getFieldValue("unifiedVisibility");
      if (isUnifiedVisibility) {
        const currentRoleVisibility = form.getFieldValue("roleVisibility");
        if (!Object.keys(currentRoleVisibility).length || force) {
          form.setFieldsValue({ roleVisibility: defaultRoleVisibility });
        }
      } else {
        const teamVisibility = form.getFieldValue("teamVisibility");
        if (!teamVisibility.length || force) {
          form.setFieldsValue({ teamVisibility: defaultTeamVisibility });
        }
      }
    }
  );

  useEffect(() => {
    if (visibility === FieldVisibility.TEAMS) {
      ensureVisibilityPresent();
    }
  }, [ensureVisibilityPresent, visibility]);

  const handlePhiEnabledChange = useCallback(
    (phiEnabled: boolean) => {
      if (phiEnabled) {
        const visibility = form.getFieldValue("visibility");
        if (visibility === FieldVisibility.FULL_ACCESS || visibility === FieldVisibility.PUBLIC) {
          form.setFieldsValue({
            visibility:
              schemaField?.category === SchemaFieldCategory.SYSTEM_REQUIRED
                ? FieldVisibility.TEAMS
                : FieldVisibility.PRIVATE,
          });
        }
        ensureVisibilityPresent({ force: true });
      }
    },
    [ensureVisibilityPresent, form, schemaField?.category]
  );

  const handleChangeSpecifyByRole = useCallback(
    (unifiedVisibility: boolean) => {
      if (unifiedVisibility) {
        ensureVisibilityPresent({ unifiedVisibility });
      } else {
        // copy unified into each team
        const roleVisibility = form.getFieldValue("roleVisibility");
        const teamVisibility = createTeamVisibilityFromRoleVisibility(roleVisibility, roles, teams);
        form.setFieldsValue({ teamVisibility });
      }
      form.setFieldsValue({ unifiedVisibility });
    },
    [ensureVisibilityPresent, form, roles, teams]
  );

  const fieldsNotSupportingPhi = useMemo(
    () =>
      selectedSchemaFields
        ? selectedSchemaFields
            .filter((row) => {
              const field = fieldModel.getByPlatformName(row.field);
              return !field?.doesSupportPhi;
            })
            .map((row) => row.field)
        : [],
    [fieldModel, selectedSchemaFields]
  );

  const privateDisabled =
    !phiEnabled &&
    !phiSupportedField &&
    !selectedSchemaFields &&
    [
      SchemaFieldCategory.RELATIONSHIP,
      SchemaFieldCategory.SYSTEM_DEPENDENT,
      SchemaFieldCategory.SYSTEM_REQUIRED,
      SchemaFieldCategory.VARIANT,
    ].includes(schemaField?.category as SchemaFieldCategory);

  const disabledFieldVisibilities = useMemo(() => {
    const visibilityOptions: FieldTeamVisibility[] = [];
    if (isDealStageFieldViewOnly) {
      visibilityOptions.push(FieldVisibility.FULL_ACCESS);
    }
    if (isDealFunnelFieldFullAccess) {
      visibilityOptions.push(FieldVisibility.PUBLIC);
    }
    if (privateDisabled) {
      visibilityOptions.push(FieldVisibility.PRIVATE);
    }
    return visibilityOptions;
  }, [isDealFunnelFieldFullAccess, isDealStageFieldViewOnly, privateDisabled]);

  return (
    <div className={styles.container}>
      {((supportsHipaa && phiSupportedField) || selectedSchemaFields) && (
        <Form.Item name="phiEnabled">
          <PhiSwitch onChange={handlePhiEnabledChange} />
        </Form.Item>
      )}

      {supportsHipaa && !phiSupportedField && !selectedSchemaFields && (
        <Alert
          className={styles.alert}
          message={intl.formatMessage(messages.noPhiSupport)}
          showIcon
          type="info"
        />
      )}

      {(isDealStageFieldViewOnly || isDealFunnelFieldFullAccess) && (
        <Alert
          className={styles.alert}
          message={intl.formatMessage(
            isDealStageFieldViewOnly ? messages.dealFunnelWarning : messages.dealStageWarning
          )}
          showIcon
          type="info"
        />
      )}

      {phiEnabled && fieldsNotSupportingPhi.length > 0 && (
        <Alert
          className={styles.bulkAlert}
          message={
            <>
              {intl.formatMessage(messages.phiWarning, { b: (text) => <b>{text}</b> })}
              <ul>
                {fieldsNotSupportingPhi.map((field) => (
                  <li key={field}>{fieldModel.getByPlatformName(field)?.displayName}</li>
                ))}
              </ul>
            </>
          }
          showIcon
          type="warning"
        />
      )}

      <Form.Item label={intl.formatMessage(messages.fieldAccess)} name="visibility">
        <VisibilityRadioGroup
          calculatedField={calculatedField}
          disabledFieldVisibilities={disabledFieldVisibilities}
          entityType={entityType}
          onChangeSpecifyByRole={handleChangeSpecifyByRole}
          phiEnabled={phiEnabled}
          selectedSchemaFields={selectedSchemaFields}
        />
      </Form.Item>

      <Form.Item hidden name="unifiedVisibility" />

      <div className={styles.teamsVisibility}>
        <Form.Item name="roleVisibility" noStyle={!roleVisibilityVisible} preserve>
          <RoleVisibility
            calculatedField={calculatedField}
            disabledFieldVisibilities={disabledFieldVisibilities}
            phiEnabled={phiEnabled}
            schemaField={schemaField}
            visible={roleVisibilityVisible}
          />
        </Form.Item>

        <Form.Item name="teamVisibility" noStyle={!teamVisibilityVisible} preserve>
          <TeamVisibility
            calculatedField={calculatedField}
            disabledFieldVisibilities={disabledFieldVisibilities}
            phiEnabled={phiEnabled}
            schemaField={schemaField}
            visible={teamVisibilityVisible}
          />
        </Form.Item>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  entityType: getActiveEntityType(state),
  roles: getRolesExcludingOwners(state),
  supportsHipaa: doesOrganizationSupportHipaa(state),
  teams: getTeams(state),
});

export default connect(mapStateToProps)(FieldAccess);
